import { format, isSameYear } from 'date-fns';
import { sortBy } from 'lodash';

import { proxyGet } from '../utils/basker-proxy';
import zonedDate from '../utils/zoned-date';
import { getTimeOfDay } from "../widgets/utils/date-formatters.mjs";

let _eventsPromise = null;

const now = zonedDate();

export function getEvents() {
    if (_eventsPromise) {
        return _eventsPromise;
    }

    _eventsPromise = Promise.all([
        proxyGet(`/event-instances.json`),
        proxyGet(`/events.json`),
    ])
        .then(([instances, events]) => {
            return instances.docs.map(instance => {
                instance = processKeywords(instance);
                const event = events.docs.find(event => event.url === instance.event.url);
                return processPerformance(instance, event);
            });
        });

    return _eventsPromise;
}

export function getPerformances() {
    return getEvents().then(response => {
        let performances = [];

        for (const bookableItem of response) {
            const data = {
                ...bookableItem,
            }

            data._auxiliary = {
                group_by_day: format(data.performancedate, 'yyyy-MM-dd'),
                group_by_month: format(data.performancedate, 'yyyy-MM'),
                group_name_month: isSameYear(now, data.performancedate) ? format(data.performancedate, 'MMMM') : format(data.performancedate, 'MMMM yyyy'),
            }

            performances.push(data);
        }

        performances = performances.filter(item => {
            return +zonedDate(item.performancedate) > +now;
        });

        return sortBy(performances, item => {
            return +item.performancedate;
        });
    });
}

/**
 * Mimic Shopify's handleize function
 *
 * https://github.com/Shopify/liquid/blob/42b6763546f218dc8ad1184e10c5e2d9a262b006/performance/shopify/shop_filter.rb#L97-L106
 */
function handleize(string) {
    if (!string) {
        return '';
    }

    return string
        .toString()
        .toLowerCase()
        .replace(/['"()[]]/g, '')
        .replace(/\W+/g, '-')
        .replace(/-+$/, '')
        .replace(/^-+/, '');
}

function processKeywords(bookableItem) {
    const categories = [];
    const eventTypes = [];
    const event = [];

    const eventTypesArray = bookableItem.attributes?.custom?.event_type ?? [];
    eventTypesArray.map(type => {
        categories.push(type);
    });

    event.push({ title: bookableItem.event.title });

    categories.length ? categories.map(category => {
        eventTypes.push({ title: category });
    }) : [];

    const ticketsUnder50 = (bookableItem.tessitura?.keywords || [])
        // .filter(kw => kw.Description === 'Ticket Bargain')
        .filter(kw => kw.Description === 'Rental ') // Dummy keyword
        .map(kw => 'under50');
    const accessClub = (bookableItem.tessitura?.keywords || [])
        // .filter(kw => kw.Description === 'Access Club')
        .filter(kw => kw.Description === 'Ailey')   // Dummy keyword
        .map(kw => 'accessClub');

    bookableItem.availability_handle = handleize(bookableItem.availability);
    const timeOfDay = getTimeOfDay(zonedDate(bookableItem.startDateLocalAsUTC));

    bookableItem.filters = {
        categories: categories,
        events_filters: event,
        event_type_filters: eventTypes,
        timeOfDay: [timeOfDay],
        ticketsUnder50,
        accessClub,
    };

    return bookableItem;
}

function processPerformance(bookableItem, event = null) {
    bookableItem.venue = bookableItem.event.venue ? bookableItem.event.venue.title : null;
    bookableItem.title = bookableItem.event.title;
    bookableItem.prefix = bookableItem.event.theme?.settings.prefix;
    bookableItem.suffix = bookableItem.event.theme?.settings.suffix;
    // bookableItem.performancedate = zonedDate(bookableItem.startDate); // this is true UTC
    bookableItem.performancedate = zonedDate(bookableItem.startDateLocalAsUTC); // this is "local time" (i.e. ignore the timezone)
    bookableItem.description = bookableItem.event.description;
    bookableItem.image = bookableItem.event?.image || null;
    bookableItem.availability = bookableItem.attributes?.custom?.availability || null;
    bookableItem.availability_handle = bookableItem.availability?.toLowerCase().replace(/\s/g, '-');

    // Primary CTA (will use theme CTA if set, otherwise till default to TNEW link & "Buy Tickets")
    if ((bookableItem.tessitura.productionSeasonId && bookableItem.tessitura.performanceId) || event?.theme?.settings?.primaryBtn) {
        bookableItem.ctaPrimary = {
            title: bookableItem.bookingLabel ?
                bookableItem.bookingLabel :
                event?.theme?.settings?.primaryBtn?.title ?
                    event?.theme?.settings?.primaryBtn?.title :
                    'Buy Tickets',
            link: event?.theme?.settings?.primaryBtn?.link ?
                event.theme?.settings?.primaryBtn?.link :
                `${AppConfig.boDomain}booking/production/${bookableItem.tessitura.performanceId}`,
        };
    }

    // Secondary CTA (will use theme CTA if set, otherwise till default to Basker event link & "More Info")
    const secondaryLink = event?.theme?.settings?.secondaryBtn?.link ?
        event?.theme?.settings?.secondaryBtn?.link :
        bookableItem.event.url ?
            bookableItem.event.url :
            null;
    if (secondaryLink) {
        bookableItem.ctaSecondary = {
            title: event?.theme?.settings?.secondaryBtn?.title ?
                event?.theme?.settings?.secondaryBtn?.title :
                'More Info',
            link: secondaryLink,
        };
    }

    const date = bookableItem.performancedate;
    const hour = format(date, 'h');
    const minutes = format(date, 'mm');
    const period = format(date, 'a').toLowerCase();

    bookableItem.formattedTime = minutes === '00' ? `${hour}${period}` : `${hour}:${minutes}${period}`;

    return bookableItem;
}
